import atNodeDB from "./database";
import scrape from "./scrapeFiracrawl";
import { ChatPromptTemplate } from '@langchain/core/prompts';
import { ChatOpenAI } from '@langchain/openai';
import { SummaryStats } from "./types";
import { emitCustomEvent } from 'react-custom-events';

class LLM {
  private _isSummarizing: boolean = false;
  private keywordsSchema = {
    name: 'Keywords',
    description: 'Keywords for an article',
    type: 'object',
    properties: {
      keywords: {
        description: 'Keywords for an article',
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
    required: ['keywords'],
  };
  private keywordsPrompt = `You are an AI designed to extract relevant keywords from text. Your task is to analyze the provided text and identify key themes, topics, or keywords that best represent the content. The keywords should be concise and relevant to the main ideas presented in the text.

For example:
- If the text is about advancements in artificial intelligence and machine learning, appropriate keywords might include: "artificial intelligence", "machine learning", "technology".
- If the text is about a recent sports event, appropriate keywords might include: "sports", "event", "team names", "location".
- If the text is about food recipes, appropriate keywords might include: "recipe", "ingredients", "cooking instructions".

Return resut in the JSON list format. Do not escape characters.
Example:
\`\`\`
keywords: ["keyword1", "keyword2", "keyword3"]
\`\`\`
`;

  private questionsSchema = {
    name: 'questions',
    description: 'Questions for an article',
    type: 'object',
    properties: {
      questions: {
        description: 'Questions for an article',
        type: 'array',
        items: {
          type: 'string',
        },
      },
    },
    required: ['questions'],
  };

  private questionsPrompt = `1.) Analyze the input text and generate essential questions that, when answered, capture the main points and core meaning of the text. 

2.) When formulating your questions: 
   a. Address the central theme or argument 
   b. Identify key supporting ideas 
   c. Highlight important facts or evidence 
   d. Reveal the author's purpose or perspective 
   e. Explore any significant implications or conclusions. 

3. ) Questions examples:
'What was the final score of the Kansas City Chiefs vs New Orleans Saints game?'
'Where can AutoGen be installed from?'
'What are the ingridients of hamburger?'
'What is the recipe for Lasagne?'


4.) Return resut in the JSON list format. Do not escape characters.
Format example:
\`\`\`
result: ["question1", "question2", "question3"  "question4", "question5"]
\`\`\`
`;

  public async summarizeUrl(url: string): Promise<SummaryStats> {

    if (this._isSummarizing) {
      console.log('Already summarizing');
      return SummaryStats.withError('Already summarizing');
    }

    try {
      this._isSummarizing = true;

      url = url.trim();

      emitCustomEvent('status', `Summarizing URL: ${url}`);

      let articlesCount = 0;
      let keywordsCount = 0;
      let questionsCount = 0;

      if (!url || !url.startsWith('http')) {
        console.log('Invalid URL:', url);
        return SummaryStats.withError('Invalid URL');
      }

      this.addToUrlBackup(url);

      const existingArticle = await atNodeDB.getArticleByUrl(url);

      if (existingArticle) {
        console.log(`Article already exists: ${existingArticle.Title}`);
        return SummaryStats.withError(`Article already exists: ${existingArticle.Title}`);
      }

      emitCustomEvent('status', `Scraping article content`);
      const scrapResult = await scrape(url);
      if (scrapResult.error) {
        console.log(`Failed to scrap: ${scrapResult.error}, url: ${url}`);
        return SummaryStats.withError(`${scrapResult.error}, url: ${url}`);
      }
      emitCustomEvent('status', `Scraped article content ${scrapResult.title}`);

      emitCustomEvent('status', `Extracting keywords`);
      const keywordIDs = await this.extractKeywords(scrapResult.markdown);
      keywordsCount += keywordIDs.length;
      emitCustomEvent('status', `Extracted ${keywordIDs.length} keywords`);

      emitCustomEvent('status', `Extracting questions`);
      const questionIDs = await this.extractQuestions(scrapResult.markdown);
      questionsCount += questionIDs.length;
      emitCustomEvent('status', `Extracted ${questionIDs.length} questions`);

      emitCustomEvent('status', `Inserting summary into the database`);
      await atNodeDB.insertSummary(scrapResult.title, url, keywordIDs, questionIDs);
      emitCustomEvent('status', `Inserted summary into the database`);            

      return new SummaryStats(articlesCount, keywordsCount, questionsCount, scrapResult.title, true);
    } 
    catch (error) {
      console.log(`Error while summarizing: ${error}, url: ${url}`);      
      return SummaryStats.withError(`${error}, url: ${url}`);
    } 
    finally {
      this._isSummarizing = false;
    }
  }

  private async extract(articleContent: string, prompt: string, schema: object): Promise<any> {

    const key = localStorage.getItem("OPENAI_API_KEY");
    const model = new ChatOpenAI({
      temperature: 0,
      maxTokens: 300,
      apiKey: key || "",
    });

    const structuredModel = model.withStructuredOutput({
      schema: schema,
      // includeRaw: true,
    });

    const promptTemplate = ChatPromptTemplate.fromMessages([
      ['system', prompt],
      ['user', '{input}'],
    ]);

    const chain = promptTemplate.pipe(structuredModel);

    let result = null;
    try {
      result = await chain.invoke({ input: articleContent });
    } catch (error) {
      console.log('Error while extracting with llm:', error);
    }

    return result;
  }

  private async extractKeywords(articleContent: string): Promise<string[]> {
    // return ['aaa', 'bbb', 'ccc'];

    let result = null;
    for (let i = 0; i < 3; i++) {
      try {
        result = await this.extract(articleContent, this.keywordsPrompt, this.keywordsSchema);
        if (result) break;
      } catch (error) {
        console.log(`ExtractKeywords retry ${i + 1} failed:`, error);
      }
    }

    if (!result) {
      return [];
    }

    return result.keywords;
  }

  private async extractQuestions(articleContent: string): Promise<string[]> {
    // return ['111', '222', '333'];

    let result = null;
    for (let i = 0; i < 3; i++) {
      try {
        result = await this.extract(articleContent, this.questionsPrompt, this.questionsSchema);
        if (result) break;
      } catch (error) {
        console.log(`ExtractQuestions retry ${i + 1} failed:`, error);
      }
    }

    if (!result) {
      return [];
    }

    return result.questions;
  }

  private addToUrlBackup(url: string) {
    const backupUrls = localStorage.getItem('backupUrls');
    if (backupUrls) {
      const urls = JSON.parse(backupUrls);
      if (!urls.includes(url)) {
        urls.push(url);
        localStorage.setItem('backupUrls', JSON.stringify(urls));
      }
    } else {
      localStorage.setItem('backupUrls', JSON.stringify([url]));
    }
  }
}

const llm = new LLM();
export default llm;

